import { useState } from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { LedgerAccountsDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  DisableLedgerAccountDocument,
  GetFinanceLedgerAccountsDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { LedgerAccountSelector } from 'modules/banking/components/LedgerAccountSelector'

interface Props {
  id: string
  onClose: () => void
}

const DisableLedgerAccountModal = ({ id, onClose }: Props) => {
  const [ledgerAccountId, setLedgerAccountId] = useState<string | undefined>(undefined)

  const { data: dataGet, loading: loadingData } = useQuery(GetFinanceLedgerAccountsDocument, {
    variables: { ids: [id] },
  })
  const ledgerAccount = dataGet?.finance.accounts[0]

  const [disableLedgerAccount, { loading }] = useMutation(DisableLedgerAccountDocument, {
    extractorFn: (data) => data.financeMutations.disableFinanceAccount,
    refetchQueries: [LedgerAccountsDataTableDocument],
  })

  const handleSubmit = async () => {
    const result = await disableLedgerAccount({
      variables: {
        id,
        ledgerAccountIdToMerge: ledgerAccountId,
      },
    })

    const handled = handleResult(result, (errors) => {
      app.setFlash({
        type: 'wrong',
        message: errors.errors || i18n.t('errors.generic_error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.chart_of_accounts.disable.success'),
      })
      onClose()
    }
  }

  return (
    <Modal
      illustration="delete-cross"
      title={i18n.t('finance.chart_of_accounts.disable.title_v3', {
        name: ledgerAccount ? ledgerAccount?.name : '',
      })}
      description={i18n.t('finance.chart_of_accounts.disable.description_v3')}
      onClose={onClose}
      onSubmit={handleSubmit}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={onClose}>
            {i18n.t('finance.buttons.cancel')}
          </Button>
          <Button disabled={loading || loadingData} submit>
            {i18n.t('finance.chart_of_accounts.disable.deactivate_and_merge')}
          </Button>
        </Box>
      }
    >
      {!loadingData && ledgerAccount && (
        <Box gap="s24">
          <Box component="ul" paddingLeft="s24" gap="s4">
            <Box
              component="li"
              style={{
                display: 'list-item',
                listStyleType: 'disc',
              }}
            >
              <Box>
                <Text.Normal size="300" color="textSecondary">
                  <Text.Normal size="300" weight="semibold" color="textSecondary">
                    {i18n.t('finance.chart_of_accounts.disable.important')}
                  </Text.Normal>{' '}
                  {i18n.t('finance.chart_of_accounts.disable.description_two_v2')}
                </Text.Normal>
              </Box>
            </Box>
            <Box component="li" style={{ display: 'list-item', listStyleType: 'disc' }}>
              <Box>
                <Text.Normal size="300" color="textSecondary">
                  {i18n.t('finance.chart_of_accounts.disable.description_three_v2')}
                </Text.Normal>
              </Box>
            </Box>
          </Box>

          <Box gap="s4">
            <Text.Normal size="200" weight="medium" overflowWrap color="textSecondary">
              {i18n.t('finance.chart_of_accounts.disable.ledger_account_to_merge_v3', {
                name: ledgerAccount.name,
              })}
            </Text.Normal>
            <LedgerAccountSelector
              legalEntityId={ledgerAccount.legalEntity?.id}
              types={[ledgerAccount.type]}
              disabled={false}
              onSelect={setLedgerAccountId}
              skipLedgerAccountIds={[id]}
            />
          </Box>
        </Box>
      )}

      {(loadingData || loading) && <Loading />}
    </Modal>
  )
}

export default observer(DisableLedgerAccountModal)
